import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Loader2, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { SECTIONS } from "@/components/sectionConfig";

const BUSY = ["suggesting", "applying"];
const title = (k) => SECTIONS.find((s) => s.key === k)?.title || k;

const Working = ({ text }) => (
  <div className="py-10 flex flex-col items-center gap-3 text-center" data-testid="improve-working">
    <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
    <p className="text-slate-300">{text}</p>
    <p className="text-xs text-slate-500">This usually takes 30 to 90 seconds. You can close this window; the work continues.</p>
  </div>
);

const Suggestions = ({ imp, score, onApply, onDiscard }) => {
  const [picked, setPicked] = useState(() => imp.suggestions.map((s) => s.id));
  const gain = imp.suggestions.filter((s) => picked.includes(s.id)).reduce((a, s) => a + s.est_gain, 0);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="space-y-4">
      {imp.error && <p className="flex gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="improve-error"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{imp.error}</p>}
      {imp.weak_areas?.length > 0 && <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Weakest areas</p>
        <ul className="max-h-32 overflow-y-auto space-y-1.5 rounded-lg border border-amber-400/20 bg-amber-400/5 p-3" data-testid="improve-weak-areas">{imp.weak_areas.map((w) => <li key={w} className="flex gap-2 text-xs text-amber-100/90"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />{w}</li>)}</ul></div>}
      <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
        {imp.suggestions.map((s) => (
          <label key={s.id} data-testid={`improve-suggestion-${s.id}`} className={`flex gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${picked.includes(s.id) ? "border-emerald-500/50 bg-emerald-500/5" : "border-slate-800 hover:border-slate-600"}`}>
            <Checkbox data-testid={`improve-check-${s.id}`} checked={picked.includes(s.id)} onCheckedChange={() => toggle(s.id)} className="mt-1" />
            <div className="flex-1 space-y-1">
              <div className="flex items-start justify-between gap-3"><p className="font-semibold text-slate-100">{s.title}</p>
                <span className="num shrink-0 text-xs font-bold text-emerald-300 rounded-full bg-emerald-500/15 px-2 py-0.5">+{s.est_gain} est.</span></div>
              <p className="text-sm text-slate-300">{s.change}</p>
              {s.why && <p className="text-xs text-slate-400"><span className="text-slate-500">Why: </span>{s.why}</p>}
              {s.tradeoff && <p className="text-xs text-amber-300/80"><span className="text-slate-500">Trade-off: </span>{s.tradeoff}</p>}
              <p className="text-[11px] text-slate-500">Rewrites: {s.sections.map(title).join(", ")}</p>
            </div>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-800 pt-4">
        <p className="text-sm text-slate-400" data-testid="improve-estimate">Estimated: <span className="num font-bold text-slate-100">{score}</span> → <span className="num font-bold text-emerald-300">{Math.min(100, score + gain)}</span>
          <span className="block text-[11px] text-slate-500">The AI re-scores honestly after rewriting; the final score may differ.</span></p>
        <div className="flex gap-2">
          <Button variant="ghost" data-testid="improve-discard" onClick={onDiscard}>Discard</Button>
          <Button data-testid="improve-apply" disabled={!picked.length} onClick={() => onApply(picked)} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><Sparkles className="h-4 w-4 mr-1" />Apply {picked.length} & re-score</Button>
        </div>
      </div>
    </div>
  );
};

const Result = ({ imp, onMore }) => {
  const d = (imp.to_score ?? 0) - (imp.from_score ?? 0);
  return (
    <div className="space-y-4" data-testid="improve-result">
      <div className="flex items-center justify-center gap-6 py-4">
        <div className="text-center"><p className="text-xs text-slate-500 mb-1">Before</p><p className="num text-4xl font-bold text-slate-400" data-testid="improve-from">{imp.from_score}</p><p className="text-xs text-slate-500">{imp.from_verdict}</p></div>
        <ArrowRight className="h-6 w-6 text-slate-500" />
        <div className="text-center"><p className="text-xs text-slate-500 mb-1">After</p><p className={`num text-4xl font-bold ${d > 0 ? "text-emerald-300" : d < 0 ? "text-rose-300" : "text-slate-200"}`} data-testid="improve-to">{imp.to_score}</p><p className="text-xs text-slate-500">{imp.to_verdict}</p></div>
      </div>
      {d < 0 && <p data-testid="improve-score-dropped" className="flex gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />The honest re-score went down. You can restore the previous version from Version history.</p>}
      {imp.summary && <p className="text-sm text-slate-300">{imp.summary}</p>}
      <p className="text-xs text-slate-500">Applied: {(imp.applied || []).join("; ")}. Rewritten sections: {(imp.sections || []).map(title).join(", ")}.</p>
      <p className="text-xs text-slate-500">Both the previous and the improved study are saved in Version history, where you can compare or restore them.</p>
      <div className="flex justify-end"><Button data-testid="improve-more" onClick={onMore} variant="outline" className="rounded-full border-slate-700 bg-transparent"><TrendingUp className="h-4 w-4 mr-1" />Find more improvements</Button></div>
    </div>
  );
};

export const ImproveScore = ({ study, onChange, autoOpen }) => {
  const [open, setOpen] = useState(false);
  const imp = study.improve || {};
  const busy = BUSY.includes(imp.status);
  useEffect(() => { if (autoOpen) click(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => api.get(`/studies/${study.id}`).then((r) => {
      onChange(r.data);
      const s = r.data.improve?.status;
      if (s === "applied") toast.success(`Feasibility score: ${r.data.improve.from_score} → ${r.data.improve.to_score}`);
      if (s === "suggested" && imp.status === "suggesting") toast.success("Improvement ideas are ready");
      if (s === "failed") toast.error("The AI could not finish, please try again");
    }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [busy, study.id, imp.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggest = async () => {
    try { await api.post(`/studies/${study.id}/improve/suggest`); onChange({ ...study, improve: { status: "suggesting" } }); setOpen(true); }
    catch (e) { toast.error(errMsg(e, "Could not start")); }
  };
  const apply = async (ids) => {
    try { await api.post(`/studies/${study.id}/improve/apply`, { ids }); onChange({ ...study, improve: { ...imp, status: "applying", chosen: ids, error: null } }); }
    catch (e) { toast.error(errMsg(e, "Could not apply")); }
  };
  const discard = async () => {
    try { await api.delete(`/studies/${study.id}/improve`); onChange({ ...study, improve: null }); setOpen(false); }
    catch (e) { toast.error(errMsg(e, "Could not discard")); }
  };
  const click = () => (imp.status && imp.status !== "failed" ? setOpen(true) : suggest());
  const label = imp.status === "suggesting" ? "Finding improvements…" : imp.status === "applying" ? "Improving study…" : imp.status === "suggested" ? "Review improvements" : "Improve feasibility score";

  return (
    <>
      <Button data-testid="improve-score-btn" onClick={click} className="rounded-full bg-amber-400 text-amber-950 hover:bg-amber-300">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}{label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-800 text-slate-100" data-testid="improve-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><TrendingUp className="h-5 w-5 text-amber-400" />Improve feasibility score</DialogTitle>
            <DialogDescription className="text-slate-400">The AI suggests real changes to the project plan. The score only goes up if the plan gets better.</DialogDescription>
          </DialogHeader>
          {imp.status === "suggesting" && <Working text="Reviewing the study and finding its weakest areas…" />}
          {imp.status === "applying" && <Working text={`Rewriting the affected sections with ${imp.chosen?.length || ""} change(s) and re-scoring…`} />}
          {imp.status === "suggested" && <Suggestions imp={imp} score={study.score ?? 0} onApply={apply} onDiscard={discard} />}
          {imp.status === "applied" && <Result imp={imp} onMore={suggest} />}
          {imp.status === "failed" && <div className="space-y-3"><p className="text-sm text-rose-300" data-testid="improve-failed">{imp.error || "Something went wrong."}</p><Button onClick={suggest} data-testid="improve-retry" className="rounded-full">Try again</Button></div>}
        </DialogContent>
      </Dialog>
    </>
  );
};
